/** Creative Library domain wire. Public tokens only; storage locators stay server-side. */
import type { CreativeFormatId } from "./creative-formats";

export const CREATIVE_MATERIAL_ROLES = [
  "product",
  "logo",
  "style",
  "composition",
  "identity",
] as const;
export type CreativeMaterialRole = (typeof CREATIVE_MATERIAL_ROLES)[number];
/** Most references one brand or Board can attach. DTOs and editors share this value. */
export const CREATIVE_REFERENCE_LIMIT = 4;
export type CreativeUsage =
  | "internal_only"
  | "external_allowed"
  | "unspecified";
export type CreativeSharingMode = "private" | "shared" | "organization";
export interface CreativePaletteColor {
  name: string;
  value: string;
}
export interface CreativeBrand {
  token: string;
  collectionToken: string;
  name: string;
  parentBrandToken: string | null;
  guidance: string;
  palette: CreativePaletteColor[];
  typography: string;
  materialTokens: string[];
  /** Attached tokens that exist but the caller cannot read. Any other unreadable token is gone. */
  hiddenReferenceTokens?: string[];
  revision: number;
  sharingMode: CreativeSharingMode;
  canEdit: boolean;
  isOwner: boolean;
  updatedAt: string;
}
export interface CreativeBrandBody {
  name: string;
  parentBrandToken?: string | null;
  guidance?: string;
  palette?: CreativePaletteColor[];
  typography?: string;
  materialTokens?: string[];
}
export interface UpdateCreativeBrandBody extends Partial<CreativeBrandBody> {
  revision: number;
}
export interface CreativeProduct {
  boardTokens?: string[];
  unavailableBoardCount?: number;
  token: string;
  brandToken: string;
  name: string;
  sku: string | null;
  description: string;
  revision: number;
  canEdit: boolean;
  updatedAt: string;
}
export interface CreativeProductBody {
  boardTokens?: string[];
  brandToken: string;
  name: string;
  sku?: string | null;
  description?: string;
}
export interface UpdateCreativeProductBody extends Partial<
  Omit<CreativeProductBody, "brandToken">
> {
  revision: number;
}
export const CREATIVE_SUBJECT_REFERENCE_VIEWS = [
  "front",
  "three_quarter",
  "profile",
  "full_body",
  "other",
] as const;
export type CreativeSubjectReferenceView =
  (typeof CREATIVE_SUBJECT_REFERENCE_VIEWS)[number];

export interface CreativeSubject {
  token: string;
  brandToken: string;
  kind: "person" | "mascot";
  name: string;
  guidance: string;
  referenceTokens: string[];
  unavailableReferenceCount?: number;
  primaryReferenceToken?: string | null;
  primaryReferenceUnavailable?: boolean;
  referenceViews?: Record<string, CreativeSubjectReferenceView>;
  revision: number;
  canEdit: boolean;
  updatedAt: string;
}
export interface CreativeSubjectBody {
  primaryReferenceToken?: string | null;
  referenceViews?: Record<string, CreativeSubjectReferenceView>;
  brandToken: string;
  kind: "person" | "mascot";
  name: string;
  guidance?: string;
  referenceTokens?: string[];
}
export interface UpdateCreativeSubjectBody extends Partial<
  Omit<CreativeSubjectBody, "brandToken">
> {
  revision: number;
}
export interface CreativeBoardSlot {
  role: CreativeMaterialRole;
  required: boolean;
}
export interface CreativeBoardDefinition {
  instructions: string;
  slots: CreativeBoardSlot[];
  referenceTokens: string[];
  defaultSize: "1024x1024" | "1536x1024" | "1024x1536";
}
export interface CreativeBoard {
  /** Authorized saved items in this Board; not a generation input. */
  creativeCount?: number;
  token: string;
  brandToken: string;
  name: string;
  description: string;
  revision: number;
  channelTags: string[];
  applicableBrandTokens: string[];
  definition: CreativeBoardDefinition;
  /** Attached tokens that exist but the caller cannot read. Any other unreadable token is gone. */
  hiddenReferenceTokens?: string[];
  canEdit: boolean;
  updatedAt: string;
}
export interface CreativeBoardBody {
  brandToken: string;
  name: string;
  description?: string;
  channelTags?: string[];
  applicableBrandTokens?: string[];
  definition: CreativeBoardDefinition;
}
export interface UpdateCreativeBoardBody extends Partial<
  Omit<CreativeBoardBody, "brandToken">
> {
  revision: number;
}
/** Original generation identity. Does not contain instructions or storage locators. */
export interface CreativeGenerationProvenance {
  requestId: string | null;
  /** The format this image was rendered in; a label resolves it. Absent on older files. */
  formatId?: CreativeFormatId | null;
  /** The thread image this one was rendered from, in another format. Absent on older files. */
  masterAssetToken?: string | null;
  sourceAssetToken: string | null;
  provenanceState: "known" | "unknown";
  editSource?: {
    kind: "thread_asset" | "web_reference";
    reference: string;
  } | null;
  sourceMaterials: Array<{
    token: string;
    revision: number;
    usage: CreativeUsage;
  }>;
  usageCeiling: CreativeUsage;
  brand: { token: string; name: string; revision: number } | null;
  product: { token: string; name: string; revision: number } | null;
  board: { token: string; name: string; revision: number } | null;
  subject?: {
    token: string;
    name: string;
    revision: number;
    kind?: "person" | "mascot";
  } | null;
  channel: string | null;
  intendedUse: "internal" | "external";
}
export interface CreativeFileGenerationMetadata {
  origin: "generated";
  provenance: CreativeGenerationProvenance;
}
export interface CreativeSourceDefaults {
  savedCreativeToken: string | null;
  brandToken: string | null;
  productToken: string | null;
  provenance: CreativeGenerationProvenance | null;
}
export interface CreativeItem {
  token: string;
  title: string;
  role: CreativeMaterialRole;
  usage: CreativeUsage;
  origin: "upload" | "generated";
  brandToken: string | null;
  productToken: string | null;
  mimeType: string | null;
  revision: number;
  sharingMode: CreativeSharingMode;
  mediaUrl: string;
  canEdit: boolean;
  isOwner: boolean;
  createdAt: string;
  generationProvenance: CreativeGenerationProvenance | null;
}
export interface CreativeItemBody {
  fileToken: string;
  title: string;
  role: CreativeMaterialRole;
  usage?: CreativeUsage;
  brandToken?: string | null;
  productToken?: string | null;
}
export interface UpdateCreativeItemBody {
  revision: number;
  title?: string;
  role?: CreativeMaterialRole;
  usage?: CreativeUsage;
  brandToken?: string | null;
  productToken?: string | null;
}
export interface CreativeCollaborator {
  userToken: string;
  name: string | null;
  email: string | null;
}
export interface CreativeSharing {
  sharingMode: CreativeSharingMode;
  collaborators: CreativeCollaborator[];
  isOwner: boolean;
}
export interface CreativeContextSelection {
  subjectToken?: string | null;
  brandProfileToken?: string | null;
  productToken?: string | null;
  boardToken?: string | null;
  referenceTokens?: string[];
  channel?: string | null;
  intendedUse?: "internal" | "external";
}
export interface CreativeLibraryPage<T> {
  items: T[];
}
